import * as React from 'react';
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';

import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { RouteMap } from '@/routes/Routes';
import { Drawer } from './styles';
import { ClinicalNotesIcon, ExportNotesIcon, SourceNotesIcon } from '@/components/Icons/Icons';
import ListButtons from './ListButtons';
import TeamsNavSection from './TeamsNavSection';
import {
  PaidOutlined,
  PersonOutlineOutlined,
  LabelOutlined,
  RocketLaunchOutlined,
  GroupsOutlined,
  InstallDesktopOutlined,
} from '@mui/icons-material';
import { useBannerStore } from '@/stores/bannerStore';
import { useAuthStore } from '@/stores/authStore';
import { BETA_FOLDERS_FEATURE_ENABLED } from '@/features/betaProjects/config';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '@/features/betaOrganizations/config';
import { useProjects } from '@/features/betaProjects/hooks/useProjects';
import { useOrganizations } from '@/features/betaOrganizations/hooks/useOrganizations';
import { isBetaUser } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { setLogoutFlag } from '@/utils/crossTabAuthSync';

type DrawerSection = 'settings' | 'projects' | null;

interface MiniDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
}

const MiniDrawer: React.FC<MiniDrawerProps> = ({ isOpen, toggleDrawer }) => {
  const navigate = useNavigate();
  const pathname = useLocation();
  const [openSection, setOpenSection] = React.useState<DrawerSection>(null);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const { headerHeight } = useBannerStore();
  const { userData } = useAuthStore();

  // Check if user has Beta Tester plan or plan-level beta features
  const { betaFeatures, features } = useCurrentPlanFeatures();
  const isBetaTester = isBetaUser(userData, betaFeatures);
  const shouldShowProjects = isBetaTester && BETA_FOLDERS_FEATURE_ENABLED && !!betaFeatures.folders;

  // Always fetch organizations when feature is enabled so non-beta users who are
  // invited to a team can see and access it.
  const { projects } = useProjects(shouldShowProjects ? userData?.id : undefined);
  const { organizations } = useOrganizations(BETA_ORGANIZATIONS_FEATURE_ENABLED);
  const shouldShowOrganizations = BETA_ORGANIZATIONS_FEATURE_ENABLED && organizations.length > 0;

  // Settings → Teams link: visible only when the plan includes teams. Existing
  // beta testers and anyone already in a team keep access so nobody is stranded.
  const shouldShowOrgSettings =
    BETA_ORGANIZATIONS_FEATURE_ENABLED &&
    (features.teamCollaboration || !!betaFeatures.organizations || organizations.length > 0);

  // "Team Notes" is a drill-in driven entirely by the URL: while on a team route
  // the Drawer swaps its main list for the team's folder panel. The route is the
  // single source of truth, so Back genuinely navigates (and a refresh stays put).
  // Opening a team note lands on /note/:id?org=<teamId> — we treat that as a team
  // route too so the drill stays put instead of dropping the user to My Notes.
  const onOrgRoute = pathname.pathname.startsWith(RouteMap.ORGANIZATIONS);
  const onTeamNote =
    pathname.pathname.startsWith(`${RouteMap.NOTE}/`) && !!new URLSearchParams(pathname.search).get('org');
  const isTeamDrill = shouldShowOrganizations && (onOrgRoute || onTeamNote);
  const singleTeam = organizations.length === 1 ? organizations[0] : null;

  const handleToggleSection = (section: DrawerSection, guard?: boolean) => {
    if (guard === false) return;
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const handleLogout = () => {
    // Clear session storage to prevent redirect after login
    sessionStorage.removeItem('redirectAfterLogin');
    // Set logout flags BEFORE navigation to prevent race condition where
    // Login's redirect effect sees userLoggedIn=true and navigates back
    sessionStorage.setItem('userRequestedLogout', 'true');
    setLogoutFlag();
    // Navigate to login page with logout flag - Login page handles all cleanup
    // including doSignOut() and Chrome extension auth clearing
    navigate(`${RouteMap.LOGIN}?logout=true`);
    setShowLogoutModal(false);
  };

  const handleCloseModal = () => {
    setShowLogoutModal(false);
  };

  // Auto-expand the Folders section based on current route.
  React.useEffect(() => {
    const path = pathname.pathname;
    if (shouldShowProjects && path.startsWith(RouteMap.FOLDERS)) {
      setOpenSection('projects');
    }
  }, [pathname.pathname, shouldShowProjects]);

  const projectButton = shouldShowProjects
    ? [
        {
          icon: <FolderOutlinedIcon />,
          text: 'Folders',
          onClick: () => handleToggleSection('projects', shouldShowProjects),
          isActive: pathname.pathname.startsWith(RouteMap.FOLDERS),
          showButtonsCollapse: openSection === 'projects',
          collapsedButtons: projects.map((project) => ({
            icon: <StyleOutlinedIcon />,
            text: project.name,
            onClick: () => navigate(`${RouteMap.FOLDERS}/${project.id}`),
            isActive: pathname.pathname === `${RouteMap.FOLDERS}/${project.id}`,
          })),
        },
      ]
    : [];

  // Tapping "Team Notes" navigates into the team — a single team goes straight to
  // its folders; with 2+ teams it goes to the team list. The route change is what
  // shows the drill panel (see isTeamDrill above).
  const handleOpenTeamNotes = () =>
    navigate(singleTeam ? `${RouteMap.ORGANIZATIONS}/${singleTeam.id}` : RouteMap.ORGANIZATIONS);

  // Flat, permanently visible note categories. "Team Notes" sits at slot #2 (only
  // for team members) as a plain item — tapping it drills into the team's folders
  // rather than expanding inline.
  const topButtons = [
    {
      icon: <ClinicalNotesIcon />,
      text: 'My Notes',
      onClick: () => navigate(RouteMap.MY_NOTES),
      isActive: pathname.pathname === RouteMap.MY_NOTES || pathname.pathname === RouteMap.NOTE,
    },
    ...(shouldShowOrganizations
      ? [
          {
            icon: <GroupsOutlined />,
            text: 'Team Notes',
            onClick: handleOpenTeamNotes,
            isActive: pathname.pathname.startsWith(RouteMap.ORGANIZATIONS),
          },
        ]
      : []),
    {
      icon: <ExportNotesIcon />,
      text: 'Private Notes',
      onClick: () => navigate(RouteMap.SHARED_WITH_ME),
      isActive: pathname.pathname === RouteMap.SHARED_WITH_ME,
    },
    {
      icon: <SourceNotesIcon />,
      text: 'Watched Notes',
      onClick: () => navigate(RouteMap.SAVED_NOTES),
      isActive: pathname.pathname === RouteMap.SAVED_NOTES,
    },
    {
      icon: <LabelOutlined />,
      text: 'Manage Tags',
      onClick: () => navigate(RouteMap.SETTINGS_TAGS),
      isActive: pathname.pathname === RouteMap.SETTINGS_TAGS,
    },
  ];

  const betaButtons = [
    {
      icon: <BugReportOutlinedIcon />,
      text: 'Beta Features',
      onClick: () => navigate(RouteMap.BETA),
      isActive: pathname.pathname === RouteMap.BETA,
    },
    ...projectButton,
  ];

  const bottomButtons = [
    {
      icon: <SettingsOutlinedIcon />,
      text: 'Settings',
      onClick: () => handleToggleSection('settings'),
      isActive:
        pathname.pathname === RouteMap.SETTINGS ||
        pathname.pathname === RouteMap.SETTINGS_DESKTOP_APP ||
        pathname.pathname === RouteMap.PLAN ||
        pathname.pathname === RouteMap.ONBOARDING ||
        pathname.pathname === RouteMap.ORGANIZATIONS,
      showButtonsCollapse: openSection === 'settings',
      collapsedButtons: [
        {
          icon: <PersonOutlineOutlined />,
          text: 'Personal Info',
          onClick: () => navigate(RouteMap.SETTINGS),
          isActive: pathname.pathname === RouteMap.SETTINGS,
        },
        {
          icon: <PaidOutlined />,
          text: 'Plan & Payments',
          onClick: () => navigate(RouteMap.PLAN),
          isActive: pathname.pathname === RouteMap.PLAN,
        },
        {
          icon: <RocketLaunchOutlined />,
          text: 'Onboarding',
          onClick: () => navigate(RouteMap.ONBOARDING),
          isActive: pathname.pathname === RouteMap.ONBOARDING,
        },
        ...(shouldShowOrgSettings
          ? [
              {
                icon: <GroupsOutlined />,
                text: 'Teams',
                onClick: () => navigate(RouteMap.ORGANIZATIONS),
                isActive: pathname.pathname === RouteMap.ORGANIZATIONS,
              },
            ]
          : []),
        ...(features.desktopDownload
          ? [
              {
                icon: <InstallDesktopOutlined />,
                text: 'Desktop App',
                onClick: () => navigate(RouteMap.SETTINGS_DESKTOP_APP),
                isActive: pathname.pathname === RouteMap.SETTINGS_DESKTOP_APP,
              },
            ]
          : []),
      ],
    },
    {
      icon: <HelpOutlineOutlinedIcon />,
      text: 'Support',
      onClick: () => navigate(RouteMap.SUPPORT),
      isActive: pathname.pathname === RouteMap.SUPPORT,
    },
    {
      icon: <LogoutOutlinedIcon />,
      text: 'Log out',
      onClick: () => setShowLogoutModal(true),
    },
    {
      icon: isOpen ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />,
      // Label doubles as the collapsed-state hover tooltip, so it has to describe
      // what the click actually does rather than always saying "Collapse".
      text: isOpen ? 'Collapse Panel' : 'Expand Panel',
      onClick: toggleDrawer,
    },
  ];

  return (
    <>
      <Drawer variant="permanent" open={isOpen}>
        <Stack justifyContent="space-between" height="100%" marginTop={`${headerHeight}px`}>
          <Stack>
            {isTeamDrill ? (
              // Drilled into Team Notes: the main list is replaced by the team's
              // folder panel (Back + folders). The bottom buttons stay put below.
              <TeamsNavSection isOpen={isOpen} organizations={organizations} />
            ) : (
              <>
                <ListButtons buttons={topButtons} isOpen={isOpen} />
                {isBetaTester && (
                  <>
                    <Divider sx={{ mx: 2, my: 0.5 }} />
                    <ListButtons buttons={betaButtons} isOpen={isOpen} />
                  </>
                )}
              </>
            )}
          </Stack>
          <ListButtons buttons={bottomButtons} isOpen={isOpen} />
        </Stack>
      </Drawer>

      <Dialog
        open={showLogoutModal}
        onClose={handleCloseModal}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Are you sure you want to log out?</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            You will be signed out of your account and redirected to the login page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLogout} variant="contained" color="primary">
            Log out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MiniDrawer;
